import { NamespaceLoggerAdapter, sendTelemetryEvent } from '@fluidx/utilities';
import { DriverInfo } from './DriverInfo';
import { loadComponentInIframeInternal } from './Iframe';
import { HostConfig, OfficeWebHostScope } from './OfficeWebHostTypes';
import { LoadInIframeResult } from './Iframe-shared/LoadInIframeResult';
// Task 4883749: Make `sourcelessIframeBundleUrl` required in future version of OWH
// When resolving that task, uncomment the following line:
// import * as localStorageKeys from './Iframe-sourceless/localStorageKeys';
import { loadComponentInSourcelessIframeInternal } from './Iframe-sourceless/Outside/loadComponentInSourcelessIframeInternal';
import { rerouteIframeToSourcelessIframe } from './Iframe-sourceless/rerouteIframeToSourcelessIframe';
import { OWHEventSubCategories, OWHGenericEvent } from './Telemetry';

/** Additional optional configuration when calling `loadComponentInIframe` */
export interface IframeLoadOptions {
  /**
   * The iframe service environment. If none specified, will default to Prod.
   * Note that if setting this parameter, and if you call "clearPersistedStorageInIframe" elsewhere
   * in your code, you will want to pass in the same iframeEnvironment to that function.
   */
  iframeEnvironment?: 'Prod' | 'Dogfood';

  /**
   * The container environment to load.
   * Will default to the same as the iframe environment (or, if that's not specified either, to Prod).
   * Note that for all practical cases, it's best for the container environment to match the iframeEnvironment
   * (which is also why, if left unspecified, it will default to whatever the iframeEnvironment is).
   */
  containerEnvironment?: 'Prod' | 'Dogfood';

  /**
   * The timeout (in milliseconds) for waiting for the inside of the iframe to load,
   * before declaring the load to have failed.
   * The default is 10000 ms (i.e., 10 seconds).
   * If the host wants to wait indefinitely and/or handle this externally
   * rather than relying on a timeout, set this property to 0 or a negative number.
   *
   * Note that for ad-hoc testing, the timeout can also be overwritten using LocalStorage flags: see
   * <https://office.visualstudio.com/OC/_wiki/wikis/Fluid%20Framework%20Experiences/15005/OfficeWebHost>
   * for more info.
   */
  iframeLoadTimeout?: number;

  /**
   * A flag to indicate whether the service worker should be skipped.
   * Defaults to "false" (i.e., service worker NOT disabled)
   */
  skipServiceWorker?: boolean;

  /**
   * [For internal or testing use only]
   * A URL override in place of the environment specified by "iframeEnvironment".
   */
  DoNotUse_iframeEnvironmentOverrideUrl?: string;

  /**
   * Title for iframe element to be read by screen readers.
   */
  // Task 4485007: When we are ready to bump OWH version, remove the "?​​" from the parameter below to make it required
  iframeTitle?: string;
}

export const DefaultIframeLoadOptions = {
  iframeLoadTimeout: 10000 /* keep this number in sync with the IframeLoadOptions description above! */
};

/**
 * Loads a component within an isolated iframe, into a provided hosting div,
 * given appropriate information and auth callbacks.
 * Failure to load will result in promise rejection with information
 * about the error.
 * @param driverInfo Driver specific information needed for performing Fluid load operations
 * @param componentDiv A div or a promise to a div that the component will be rendered into
 * @param scope An IFluidObject object that will be available to all containers/components loaded
 * @param config Additional information allowing a host to have richer integration with how components are initialized
 * @param iframeUrl DEPRECATED: An iframe override URL.
 *   Please use "options.iframeEnvironment" instead.
 *   This parameter will be removed in the next major-version bump of Office Web Host.
 * @param options Additional options
 */
export function loadComponentInIframe(
  driverInfo: DriverInfo,
  componentDiv: HTMLElement,
  scope: OfficeWebHostScope,
  config: HostConfig = {},
  options: IframeLoadOptions = {}
): LoadInIframeResult {
  // The moment we should consider the start of office-web-host execution, which is used for end to end performance analysis.
  const bootstrapperStartTime = Date.now();

  // We set the container environment to match the iframe environment as a reasonable default.
  const containerEnvironment = options.containerEnvironment ?? options.iframeEnvironment;

  if (config.codeLoader) {
    console.warn('Iframes do not support "HostConfig.codeLoader". The code loader will be ignored.');
  }

  const { codeLoader, logger: hostLogger, ...newConfig } = config;
  const logger =
    hostLogger &&
    new NamespaceLoggerAdapter('OfficeWebHost', hostLogger, {
      inIframe: true
    });

  // In order to allow "dark-deployment" of the sourceless iframe code, while also allowing us
  // to experiment with it locally, we're introducing a localStorage flag to allow re-routing
  // the "loadComponentInIframe" flow into to the sourceless world.
  if (rerouteIframeToSourcelessIframe()) {
    // Log a generic event to correlate QoS telemetry with dev-flow scenarios
    // Task 4544147: Consider adding a helper method for the same
    logger &&
      sendTelemetryEvent(logger, {
        eventName: OWHGenericEvent.SourcelessIframeOverrideUsed,
        subCategory: OWHEventSubCategories.LocalStorageOverride
      });

    // Task 4883749: Make `sourcelessIframeBundleUrl` required in future version of OWH
    // When resolving this task, add "safeLocalStorage" to the `'@fluidx/utilities'` imports,
    // and also uncomment the following code block:
    // const localStorageIframeUrlOverrideKey = localStorageKeys.fluidSourcelessIframeUrlOverride;
    // const sourcelessIframeBundleUrl = safeLocalStorage()?.[localStorageIframeUrlOverrideKey];
    // if (sourcelessIframeBundleUrl) {
    //   throw new Error(
    //     `When setting "${localStorageKeys.fluidRerouteIframeToSourceless}", must set "${localStorageIframeUrlOverrideKey}" as well.`
    //   );
    // }

    return loadComponentInSourcelessIframeInternal(
      { type: 'Load', driverInfo },
      componentDiv,
      scope,
      {
        ...config,
        // Pass the original *hostLogger* to sourceless iframes, so that we don't end up with both
        // "inIframe" and "inSourcelessIframe" in the same telemetry item.
        // `loadComponentInSourcelessIframeInternal` will add its own namespace adapter.
        logger: hostLogger,
        // Sourceless iframes do not support having a "hostConfig.codeLoader", and would throw an error if one is passed in to them.
        // Regular iframes don't either, and would have already shown a warning anyway.
        // So when redirecting to the sourceless iframe world, just override the codeLoader (if any) with undefined
        codeLoader: undefined
      },
      {
        containerEnvironment
        // Task 4883749: Make `sourcelessIframeBundleUrl` required in future version of OWH
        // When resolving that task, add a comma to the end of the line above, and uncomment the next line:
        // sourcelessIframeBundleUrl
      },
      {
        bootstrapperStartTime,
        isRedirectingFromRegularIframe: true
      }
    );
  }

  return loadComponentInIframeInternal(
    { type: 'Load', driverInfo },
    scope.TokenProvider,
    scope,
    componentDiv,
    newConfig,
    logger,
    bootstrapperStartTime,
    { ...options, containerEnvironment }
  );
}

// cspell:ignore bootstrapper srcdoc iframes
